import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import useEmblaCarousel from 'embla-carousel-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store/use-store'
import useStableViewport from '@/hooks/use-stable-viewport'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

gsap.registerPlugin(ScrollTrigger)

type EditorialHorizontalGalleryProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    collection: WPImage[]
  }>

const EditorialHorizontalGallery = ({
  title,
  collection,
  ...props
}: EditorialHorizontalGalleryProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useStableViewport()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const pointerDownRef = useRef(false)

  const { strings } = useStore()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    active: (collection?.length ?? 0) > 1
  })

  useEffect(() => {
    if (!emblaApi || !isMobile) return

    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
    }

    const onPointerDown = () => {
      pointerDownRef.current = true
    }

    const onPointerUp = () => {
      pointerDownRef.current = false
    }

    const onScroll = () => {
      if (pointerDownRef.current) {
        setIsDragging(true)
      }
    }

    const onSettle = () => setIsDragging(false)

    emblaApi.on('select', onSelect)
    emblaApi.on('pointerDown', onPointerDown)
    emblaApi.on('pointerUp', onPointerUp)
    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettle)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('pointerDown', onPointerDown)
      emblaApi.off('pointerUp', onPointerUp)
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettle)
    }
  }, [emblaApi, isMobile])

  const mobileGalleryRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!isMobile || !mobileGalleryRef.current) return

      gsap.fromTo(
        mobileGalleryRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileGalleryRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: mobileGalleryRef,
      dependencies: [collection, isMobile],
      revertOnUpdate: true
    }
  )

  useGSAP(
    () => {
      if (isMobile) return

      const sticky = stickyRef.current
      const section = sectionRef.current

      if (!sticky || !section || !collection) return

      const selector = gsap.utils.selector(sticky)

      const items = selector('[data-items]')[0]
      const titleEl = selector('[data-title]')[0]

      const itemsWidth = items.scrollWidth

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          toggleActions: 'play reverse play reverse',
          scrub: true,
          invalidateOnRefresh: true
        }
      })

      titleEl &&
        timeline.to(titleEl, {
          x: '16vw',
          ease: 'none'
        })

      items &&
        timeline.to(
          items,
          {
            x: -itemsWidth - window.innerWidth * 0.5,
            ease: 'none'
          },
          0
        )

      return () => {
        timeline.kill()
      }
    },

    {
      scope: stickyRef,
      dependencies: [collection, title, isMobile],
      revertOnUpdate: true
    }
  )

  if (!collection?.length) return null

  const renderDesktop = () => (
    <Section
      ref={sectionRef}
      {...props}
      className="relative max-w-full"
      style={{
        height: `calc((var(--lvh))*${collection.length * 80})`
      }}
    >
      <div
        ref={stickyRef}
        data-motion-gallery
        className="sticky top-0 h-[calc(100*var(--lvh))] w-full max-w-full overflow-hidden"
      >
        <div data-horizontal-wheel-lock className="overflow-x-clip">
          {title ? (
            <div
              className={cn(
                'pointer-events-none absolute left-0 top-0 z-0 flex h-full w-full items-center',
                'translate-x-[50vw]'
              )}
              data-title
            >
              <div
                className={cn(
                  'whitespace-nowrap opacity-10',
                  'text-[139px] md:text-d-huge',
                  'text-text-primary-light dark:text-text-primary-dark'
                )}
              >
                {title || strings?.labels?.gallery_details}
              </div>
            </div>
          ) : null}

          <div
            className="flex h-[calc(100*var(--lvh))] items-center gap-spacing-2xs"
            data-items
          >
            {collection.map((item, index) => (
              <div
                key={`image-gallery-${index}`}
                className={cn(
                  'item relative cursor-pointer bg-background-frame-light dark:bg-background-frame-dark',
                  'aspect-[0.8] h-[493px]',
                  'translate-x-[100vw]'
                )}
              >
                <img
                  src={item?.url}
                  alt={item?.alt || ''}
                  data-lightbox
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  )

  const renderMobile = () => (
    <Section {...props} className="px-padding-sm py-spacing-xl">
      <div ref={mobileGalleryRef} data-motion-gallery className="opacity-0">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {collection.map((item, index) => (
              <div
                key={`image-gallery-mobile-${index}`}
                className="w-full shrink-0"
              >
                <div className="relative aspect-[0.8] w-full bg-background-frame-light dark:bg-background-frame-dark">
                  <img
                    src={item?.url}
                    alt={item?.alt || ''}
                    data-lightbox
                    className={cn(
                      'h-full w-full object-cover',
                      isDragging && 'pointer-events-none'
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        {collection.length > 1 && (
          <div className="flex items-center justify-between pt-spacing-sm">
            <Typography variant="large" className="condensed">
              {(activeIndex + 1).toString().padStart(2, '0')}/
              {collection.length.toString().padStart(2, '0')}
            </Typography>
            <div className="flex items-end justify-end gap-spacing-2xs self-end">
              <button
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border-border-secondary-light dark:border-border-secondary-dark"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label={
                  strings?.labels?.gallery_prev || 'Immagine precedente'
                }
              >
                <SVGIcon symbol="arrow-prev" className="size-[12px]" />
              </button>
              <button
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border-border-secondary-light dark:border-border-secondary-dark"
                onClick={() => emblaApi?.scrollNext()}
                aria-label={
                  strings?.labels?.gallery_next || 'Immagine successiva'
                }
              >
                <SVGIcon symbol="arrow-next" className="size-[12px]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  )

  return <>{isMobile ? renderMobile() : renderDesktop()}</>
}

export default EditorialHorizontalGallery
